import type { MapBounds, MapboxBounds, LngLat, MapPadding } from './types'

interface ParseMapBoundsOptions {
  preferLegacyWhenAmbiguous?: boolean
}

export function normaliseLongitude(longitude: number): number {
  if (longitude > 180) {
    return longitude - 360
  }

  if (longitude < -180) {
    return longitude + 360
  }

  return longitude
}

export function getLongitudeSpan(east: number, west: number): number {
  if (east < 0 && west > 0) {
    return (180 - west) + (180 + east)
  }

  return east - west
}

export function createMapBounds(coordinates: Array<LngLat>): MapBounds | undefined {
  if (coordinates.length === 0) {
    return undefined
  }

  const latitudes = coordinates.map(({ lat }) => lat)
  const longitudes = coordinates
    .map(({ lng }) => normaliseLongitude(lng))
    .sort((left, right) => left - right)

  let west = longitudes[0]
  let east = longitudes[longitudes.length - 1]

  if (longitudes.length > 1) {
    let largestGap = -1
    let wrappedWestIndex = 0

    for (let index = 0; index < longitudes.length; index++) {
      const currentLongitude = longitudes[index]
      const nextLongitude = index === longitudes.length - 1
        ? longitudes[0] + 360
        : longitudes[index + 1]
      const gap = nextLongitude - currentLongitude

      if (gap > largestGap) {
        largestGap = gap
        wrappedWestIndex = (index + 1) % longitudes.length
      }
    }

    west = longitudes[wrappedWestIndex]
    east = longitudes[(wrappedWestIndex + longitudes.length - 1) % longitudes.length]
  }

  return {
    west,
    south: Math.min(...latitudes),
    east,
    north: Math.max(...latitudes),
  }
}

function isLongitudeWithinBounds(longitude: number, west: number, east: number): boolean {
  if (west <= east) {
    return longitude >= west && longitude <= east
  }

  return longitude >= west || longitude <= east
}

export function isCoordinateWithinBounds(coordinate: LngLat, bounds: MapBounds): boolean {
  const normalisedLongitude = normaliseLongitude(coordinate.lng)

  return coordinate.lat >= bounds.south &&
    coordinate.lat <= bounds.north &&
    isLongitudeWithinBounds(normalisedLongitude, bounds.west, bounds.east)
}

export function mapBoundsToMapboxBounds(bounds: MapBounds): MapboxBounds {
  return [
    [bounds.west, bounds.south],
    [bounds.east, bounds.north],
  ]
}

export function mapboxBoundsToMapBounds(bounds: {
  getWest(): number
  getSouth(): number
  getEast(): number
  getNorth(): number
}): MapBounds {
  return {
    west: bounds.getWest(),
    south: bounds.getSouth(),
    east: bounds.getEast(),
    north: bounds.getNorth(),
  }
}

function isFiniteMapBounds(bounds: MapBounds): boolean {
  return Number.isFinite(bounds.west) &&
    Number.isFinite(bounds.south) &&
    Number.isFinite(bounds.east) &&
    Number.isFinite(bounds.north)
}

function createMapBoundsFromParts(
  south: number,
  west: number,
  north: number,
  east: number,
): MapBounds {
  return {
    west,
    south,
    east,
    north,
  }
}

export function parseMapBounds(
  bounds: string,
  coordinates: Array<LngLat> = [],
  options: ParseMapBoundsOptions = {},
): MapBounds {
  const [first, second, third, fourth] = bounds.split(',').map(Number)

  const isLatitude = (value: number) => value >= -90 && value <= 90
  const isLongitude = (value: number) => value >= -180 && value <= 180
  const isCanonicalBounds = (
    south: number,
    west: number,
    north: number,
    east: number,
  ) => isLatitude(south) &&
    isLongitude(west) &&
    isLatitude(north) &&
    isLongitude(east) &&
    south <= north

  const candidates: Array<MapBounds> = []

  if (isCanonicalBounds(first, second, third, fourth)) {
    candidates.push(createMapBoundsFromParts(first, second, third, fourth))
  }

  // Temporary backwards compatibility for map bounds strings that were written
  // in west,south,east,north order during the migration. When both shapes are
  // valid, prefer the one that best matches the provided coordinates.
  if (isCanonicalBounds(second, first, fourth, third)) {
    const legacyCandidate = createMapBoundsFromParts(second, first, fourth, third)
    const isDuplicate = candidates.some(candidate =>
      candidate.west === legacyCandidate.west &&
      candidate.south === legacyCandidate.south &&
      candidate.east === legacyCandidate.east &&
      candidate.north === legacyCandidate.north,
    )

    if (!isDuplicate) {
      candidates.push(legacyCandidate)
    }
  }

  if (candidates.length === 0) {
    return createMapBoundsFromParts(first, second, third, fourth)
  }

  if (candidates.length === 1) {
    return candidates[0]
  }

  if (coordinates.length === 0) {
    return options.preferLegacyWhenAmbiguous ? candidates[candidates.length - 1] : candidates[0]
  }

  let bestCandidate = candidates[0]
  let bestMatchCount = coordinates.filter(coordinate => isCoordinateWithinBounds(coordinate, bestCandidate)).length

  for (let index = 1; index < candidates.length; index++) {
    const candidate = candidates[index]
    const matchCount = coordinates.filter(coordinate => isCoordinateWithinBounds(coordinate, candidate)).length

    if (matchCount > bestMatchCount) {
      bestCandidate = candidate
      bestMatchCount = matchCount
    }
  }

  return bestCandidate
}

export function serialiseMapBounds(bounds: MapBounds): string {
  return `${bounds.south},${bounds.west},${bounds.north},${bounds.east}`
}

export function subtractPaddingFromBounds(
  bounds: MapBounds,
  boundingRect: DOMRect,
  padding: MapPadding,
): MapBounds {
  // During responsive layout transitions the map container can briefly report a
  // zero-sized rect. Returning the original bounds avoids dividing by zero and
  // leaking Infinity into the bounds URL while the layout is still settling.
  if (
    !isFiniteMapBounds(bounds) ||
    !Number.isFinite(boundingRect.width) ||
    !Number.isFinite(boundingRect.height) ||
    boundingRect.width <= 0 ||
    boundingRect.height <= 0
  ) {
    return bounds
  }

  const longitudeSpan = getLongitudeSpan(bounds.east, bounds.west)
  const latitudeSpan = bounds.north - bounds.south

  const longitudeDegreesPerPixel = longitudeSpan / boundingRect.width
  const latitudeDegreesPerPixel = latitudeSpan / boundingRect.height

  const paddingTop = latitudeDegreesPerPixel * padding.top
  const paddingBottom = latitudeDegreesPerPixel * padding.bottom
  const paddingLeft = longitudeDegreesPerPixel * padding.left
  const paddingRight = longitudeDegreesPerPixel * padding.right

  return {
    west: normaliseLongitude(bounds.west + paddingLeft),
    south: bounds.south + paddingBottom,
    east: normaliseLongitude(bounds.east - paddingRight),
    north: bounds.north - paddingTop,
  }
}
